/**
 * Performance Monitoring Hook
 * Tracks Web Vitals and custom performance metrics
 */

import { useEffect } from 'react';
import { trackEvent } from './analytics';

interface PerformanceMetric {
  name: string;
  value: number;
  rating: 'good' | 'needs-improvement' | 'poor';
}

/**
 * Get Web Vitals rating based on thresholds
 */
function getVitalRating(name: string, value: number): 'good' | 'needs-improvement' | 'poor' {
  const thresholds: Record<string, [number, number]> = {
    CLS: [0.1, 0.25],
    FID: [100, 300],
    LCP: [2500, 4000],
    FCP: [1800, 3000],
    TTFB: [800, 1800],
  };

  const [good, poor] = thresholds[name] || [0, 0];
  if (value <= good) return 'good';
  if (value <= poor) return 'needs-improvement';
  return 'poor';
}

/**
 * Track Web Vitals using Performance Observer API
 */
export function usePerformanceMonitoring() {
  useEffect(() => {
    // Only run in browser
    if (typeof window === 'undefined') return;

    // Track page load time
    if (window.performance && window.performance.timing) {
      const timing = window.performance.timing;
      const loadTime = timing.loadEventEnd - timing.navigationStart;
      
      if (loadTime > 0) {
        trackEvent('performance_page_load', {
          load_time: loadTime,
          rating: getVitalRating('TTFB', loadTime),
        });
      }
    }

    // Track Largest Contentful Paint (LCP)
    if ('PerformanceObserver' in window) {
      try {
        const lcpObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          const lastEntry = entries[entries.length - 1] as any;
          
          if (lastEntry) {
            const metric: PerformanceMetric = {
              name: 'LCP',
              value: lastEntry.renderTime || lastEntry.loadTime,
              rating: getVitalRating('LCP', lastEntry.renderTime || lastEntry.loadTime),
            };
            
            trackEvent('performance_lcp', {
              value: metric.value,
              rating: metric.rating,
            });
          }
        });
        
        lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] });
      } catch (e) {
        // Observer not supported
      }

      // Track First Input Delay (FID)
      try {
        const fidObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          entries.forEach((entry: any) => {
            const metric: PerformanceMetric = {
              name: 'FID',
              value: entry.processingStart - entry.startTime,
              rating: getVitalRating('FID', entry.processingStart - entry.startTime),
            };
            
            trackEvent('performance_fid', {
              value: metric.value,
              rating: metric.rating,
            });
          });
        });
        
        fidObserver.observe({ entryTypes: ['first-input'] });
      } catch (e) {
        // Observer not supported
      }

      // Track Cumulative Layout Shift (CLS)
      try {
        let clsValue = 0;
        const clsObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          entries.forEach((entry: any) => {
            if (!entry.hadRecentInput) {
              clsValue += entry.value;
            }
          });
        });
        
        clsObserver.observe({ entryTypes: ['layout-shift'] });

        // Report CLS on page unload
        window.addEventListener('beforeunload', () => {
          const metric: PerformanceMetric = {
            name: 'CLS',
            value: clsValue,
            rating: getVitalRating('CLS', clsValue),
          };
          
          trackEvent('performance_cls', {
            value: metric.value,
            rating: metric.rating,
          });
        });
      } catch (e) {
        // Observer not supported
      }
    }

    // Track widget-specific metrics
    const widgetLoadStart = performance.now();
    
    // Track when widget becomes interactive
    const checkInteractive = () => {
      const widgetLoadTime = performance.now() - widgetLoadStart;
      trackEvent('performance_widget_load', {
        load_time: widgetLoadTime,
        rating: widgetLoadTime < 1000 ? 'good' : widgetLoadTime < 2500 ? 'needs-improvement' : 'poor',
      });
    };

    // Check after a short delay to ensure widget is fully rendered
    setTimeout(checkInteractive, 100);

  }, []);
}

/**
 * Track custom performance metrics
 */
export function trackCustomMetric(name: string, value: number, metadata?: Record<string, any>) {
  trackEvent(`performance_${name}`, {
    value,
    ...metadata,
  });
}
